import { useState, useEffect, useCallback } from 'react';
import { Tag, Trash2, Plus, FlaskConical, AlertTriangle, Check, Power, PowerOff } from 'lucide-react';
import { marginCorrectionsApi } from '../../services/marginCorrectionsApi';
import { SearchableSelect } from '../ui/SearchableSelect';
import { useToast } from '../ui/Toast';
import type { SpecialLot, LotRule, LotRuleMatchMode, LotRulePreviewResponse, MCFilterOptions } from '../../types/marginCorrections';

const fmt = (n: number | null, d = 2) => (n == null ? '–' : n.toLocaleString('de-CH', { minimumFractionDigits: d, maximumFractionDigits: d }));

interface Props {
    company: string;
    filterOptions: MCFilterOptions | null;
    canEdit: boolean;
    refreshKey: number;
    onDataChanged: () => void;
}

const MATCH_MODES: LotRuleMatchMode[] = ['Prefix', 'Suffix', 'Contains'];

export function SpecialLotsTab({ company, filterOptions, canEdit, refreshKey, onDataChanged }: Props) {
    const { toast } = useToast();
    const [lots, setLots] = useState<SpecialLot[]>([]);
    const [rules, setRules] = useState<LotRule[]>([]);
    const [loading, setLoading] = useState(false);

    // Manual-lot form
    const [showLotForm, setShowLotForm] = useState(false);
    const [lotCode, setLotCode] = useState('');
    const [lotReceipt, setLotReceipt] = useState('');
    const [lotCustomer, setLotCustomer] = useState('');
    const [lotPrice, setLotPrice] = useState('');
    const [lotNotes, setLotNotes] = useState('');
    const [savingLot, setSavingLot] = useState(false);

    // Rule editor + dry run
    const [ruleName, setRuleName] = useState('');
    const [ruleMode, setRuleMode] = useState<LotRuleMatchMode>('Suffix');
    const [rulePattern, setRulePattern] = useState('');
    const [preview, setPreview] = useState<LotRulePreviewResponse | null>(null);
    const [previewing, setPreviewing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [lotsRes, rulesRes] = await Promise.all([
                marginCorrectionsApi.listSpecialLots(company),
                marginCorrectionsApi.listLotRules(company),
            ]);
            setLots(lotsRes.lots);
            setRules(rulesRes.rules);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load special lots', 'error');
        } finally {
            setLoading(false);
        }
    }, [company, toast]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const saveLot = async () => {
        if (!lotCode.trim()) return;
        setSavingLot(true);
        try {
            await marginCorrectionsApi.saveSpecialLot({
                company,
                lotCode: lotCode.trim(),
                receiptNo: lotReceipt.trim() || null,
                intendedCustomerID: lotCustomer || null,
                negotiatedUnitCostEUR: lotPrice ? parseFloat(lotPrice) : null,
                notes: lotNotes.trim() || null,
            });
            toast('Special-price lot marked', 'success');
            setShowLotForm(false);
            setLotCode(''); setLotReceipt(''); setLotCustomer(''); setLotPrice(''); setLotNotes('');
            load();
            onDataChanged();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to save lot', 'error');
        } finally {
            setSavingLot(false);
        }
    };

    const removeLot = async (id: number) => {
        try {
            await marginCorrectionsApi.deleteSpecialLot(id);
            toast('Marking removed', 'success');
            load();
            onDataChanged();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to remove', 'error');
        }
    };

    const runPreview = async () => {
        if (!rulePattern.trim()) return;
        setPreviewing(true);
        setPreview(null);
        try {
            const res = await marginCorrectionsApi.previewLotRule({ company, matchMode: ruleMode, pattern: rulePattern.trim() });
            setPreview(res);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Preview failed', 'error');
        } finally {
            setPreviewing(false);
        }
    };

    const saveRule = async (activate: boolean) => {
        if (!ruleName.trim() || !rulePattern.trim()) { toast('Rule name and pattern are required', 'error'); return; }
        const needsForce = activate && (preview?.exceedsSafeLimit ?? false);
        if (needsForce && !window.confirm(`This rule matches ${preview?.matchCount} lots — well above the safe limit of ${preview?.maxMatchesWithoutForce}. Activate anyway?`)) return;
        try {
            await marginCorrectionsApi.saveLotRule({ company, ruleName: ruleName.trim(), matchMode: ruleMode, pattern: rulePattern.trim(), isActive: activate }, needsForce);
            toast(activate ? 'Rule saved and activated' : 'Rule saved (inactive)', 'success');
            setRuleName(''); setRulePattern(''); setPreview(null);
            load();
            onDataChanged();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to save rule', 'error');
        }
    };

    const toggleRule = async (rule: LotRule) => {
        try {
            await marginCorrectionsApi.saveLotRule({
                id: rule.id, company, ruleName: rule.ruleName, matchMode: rule.matchMode,
                pattern: rule.pattern, isActive: !rule.isActive,
            });
            toast(rule.isActive ? 'Rule deactivated' : 'Rule activated', 'success');
            load();
            onDataChanged();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to toggle rule', 'error');
        }
    };

    const removeRule = async (id: number) => {
        try {
            await marginCorrectionsApi.deleteLotRule(id);
            toast('Rule deleted', 'success');
            load();
            onDataChanged();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to delete rule', 'error');
        }
    };

    if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Manual markings */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><Tag className="w-4 h-4 text-purple-500" /> Marked lots ({lots.length})</h3>
                    {canEdit && !showLotForm && (
                        <button onClick={() => setShowLotForm(true)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"><Plus className="w-4 h-4" /> Mark lot</button>
                    )}
                </div>

                {showLotForm && (
                    <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
                        <input value={lotCode} onChange={e => setLotCode(e.target.value)} placeholder="Lot code *" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
                        <input value={lotReceipt} onChange={e => setLotReceipt(e.target.value)} placeholder="Goods receipt no. (pin to one receipt — for mixed lots)" className="w-full border rounded px-2 py-1.5 text-sm" />
                        <SearchableSelect options={filterOptions?.customers ?? []} value={lotCustomer} onChange={setLotCustomer} placeholder="Intended customer (optional)" className="w-full" />
                        <input value={lotPrice} onChange={e => setLotPrice(e.target.value)} type="number" step="0.01" placeholder="Negotiated unit cost (optional)" className="w-full border rounded px-2 py-1.5 text-sm text-right" />
                        <input value={lotNotes} onChange={e => setLotNotes(e.target.value)} placeholder="Notes (optional)" className="w-full border rounded px-2 py-1.5 text-sm" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowLotForm(false)} className="px-3 py-1.5 text-sm text-gray-600 border rounded hover:bg-white">Cancel</button>
                            <button onClick={saveLot} disabled={!lotCode.trim() || savingLot} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40">Save</button>
                        </div>
                    </div>
                )}

                <div className="space-y-2">
                    {lots.length === 0 && !showLotForm && <p className="text-sm text-gray-400 py-4 text-center">No lots marked yet. On day one the correction picker falls back to all invoices.</p>}
                    {lots.map(lot => (
                        <div key={lot.id} className="border rounded-lg p-3 flex items-start justify-between">
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <span className="font-mono text-sm font-medium">{lot.lotCode}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${lot.scope === 'receipt' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>{lot.scope === 'receipt' ? `receipt ${lot.receiptNo || lot.inboundEntryNo}` : 'whole lot'}</span>
                                    {lot.hasMixedPurchasePrice && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> mixed price</span>}
                                </div>
                                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                    {lot.intendedCustomerName && <div>For: {lot.intendedCustomerName}</div>}
                                    <div>{fmt(lot.soldQuantity, 0)} sold across {lot.soldLineCount} lines · purchase {fmt(lot.minPurchaseUnitCostEUR)}{lot.minPurchaseUnitCostEUR !== lot.maxPurchaseUnitCostEUR ? `–${fmt(lot.maxPurchaseUnitCostEUR)}` : ''}/u{lot.negotiatedUnitCostEUR != null && <> · negotiated {fmt(lot.negotiatedUnitCostEUR)}</>}</div>
                                    {lot.notes && <div className="italic">{lot.notes}</div>}
                                </div>
                            </div>
                            {canEdit && <button onClick={() => removeLot(lot.id)} className="text-gray-300 hover:text-red-500 p-1 shrink-0"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Rules */}
            <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><FlaskConical className="w-4 h-4 text-blue-500" /> Detection rules ({rules.length})</h3>

                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2 flex items-start gap-1.5">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>28.8&nbsp;% of real lot codes contain special characters. Always dry-run before activating — a careless <span className="font-mono">_</span> or <span className="font-mono">%</span> can match every lot.</span>
                </div>

                {canEdit && (
                    <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
                        <input value={ruleName} onChange={e => setRuleName(e.target.value)} placeholder="Rule name *" className="w-full border rounded px-2 py-1.5 text-sm" />
                        <div className="flex gap-2">
                            <select value={ruleMode} onChange={e => { setRuleMode(e.target.value as LotRuleMatchMode); setPreview(null); }} className="border rounded px-2 py-1.5 text-sm bg-white">
                                {MATCH_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <input value={rulePattern} onChange={e => { setRulePattern(e.target.value); setPreview(null); }} placeholder="Pattern, e.g. *" className="flex-1 border rounded px-2 py-1.5 text-sm font-mono" />
                            <button onClick={runPreview} disabled={!rulePattern.trim() || previewing} className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"><FlaskConical className="w-3.5 h-3.5" /> {previewing ? '…' : 'Dry run'}</button>
                        </div>

                        {preview && (
                            <div className={`rounded-md p-2.5 text-xs ${preview.exceedsSafeLimit ? 'bg-red-50 border border-red-200' : preview.matchCount === 0 ? 'bg-gray-100' : 'bg-green-50 border border-green-200'}`}>
                                <div className="flex items-center justify-between font-medium">
                                    <span>{preview.matchCount} of {preview.totalLotCount} lots ({preview.matchPct}%)</span>
                                    {preview.exceedsSafeLimit && <span className="text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> over limit</span>}
                                </div>
                                {preview.warning && <p className="mt-1 text-gray-600">{preview.warning}</p>}
                                {preview.sample.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {preview.sample.slice(0, 12).map(s => <span key={s.lotCode} className="font-mono text-[11px] bg-white border rounded px-1.5 py-0.5">{s.lotCode}</span>)}
                                        {preview.sampleTruncated && <span className="text-gray-400 text-[11px]">…and more</span>}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <button onClick={() => saveRule(false)} disabled={!ruleName.trim() || !rulePattern.trim()} className="px-3 py-1.5 text-sm border rounded bg-white hover:bg-gray-50 disabled:opacity-40">Save inactive</button>
                            <button onClick={() => saveRule(true)} disabled={!ruleName.trim() || !rulePattern.trim() || !preview} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40" title={!preview ? 'Dry-run first' : ''}>Save &amp; activate</button>
                        </div>
                    </div>
                )}

                <div className="space-y-2">
                    {rules.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No rules defined.</p>}
                    {rules.map(rule => (
                        <div key={rule.id} className={`border rounded-lg p-3 ${rule.isActive ? 'border-green-200 bg-green-50' : ''}`}>
                            <div className="flex items-center justify-between">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-sm font-medium">{rule.ruleName}</span>
                                        {rule.isActive
                                            ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 flex items-center gap-0.5"><Check className="w-3 h-3" /> active · {rule.matchedLotCount} lots</span>
                                            : <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">inactive</span>}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5"><span className="font-mono">{rule.matchMode}</span> · <span className="font-mono">{rule.pattern}</span></p>
                                </div>
                                {canEdit && (
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button onClick={() => toggleRule(rule)} className="text-gray-400 hover:text-gray-700 p-1" title={rule.isActive ? 'Deactivate' : 'Activate'}>
                                            {rule.isActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                                        </button>
                                        <button onClick={() => removeRule(rule.id)} className="text-gray-300 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
